/**
 * ActivityAuditExecutive — Executive Activity Audit view.
 *
 * LOCKED DECISIONS:
 *   A5  ZERO individual attribution — no names, no per-rep rows, no drilldown
 *       to specific users. Aggregate org-level signals ONLY.
 *       Role switcher accesses Manager view for per-rep data.
 *
 * This view is a scaffold placeholder — org aggregates displayed, clearly
 * labeled as Beta.
 *
 * TENANT ISOLATION: tenant_id != 2 enforced.
 * DATA STATUS: Mock/seeded.
 */

import { useState, useEffect, useMemo } from "react";
import { tokens } from "@/components/operator/tokens";
import AuditWindowSelector from "./AuditWindowSelector";
import AuditKpiCard from "./AuditKpiCard";
import { supabase, getSession } from "@/lib/session";
import type { TimeWindow } from "./audit-mock-data";

// Track D rebuild (B10, 2026-05-14): removed EXECUTIVE_KPI_MOCK (hardcoded
// org_conversations=10, pipeline_coverage=2.8x) and EXECUTIVE_DAILY_SUMMARY
// (5 fabricated date rows). Real pipeline_coverage = 1.66x (verified vs Supabase).
// activity_log has 0 rows — all KPIs show 0 until C5/C6/C7 write paths are connected.

interface DailyRow {
  date: string;
  conversations: number;
  meetings_booked: number;
  deals_advanced: number;
  deals_closed: number;
}

function windowStartExec(w: TimeWindow): Date {
  const now = new Date();
  if (w === "today") { const d = new Date(now); d.setHours(0,0,0,0); return d; }
  if (w === "week")  { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
  if (w === "month") { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
  const d = new Date(now); d.setDate(d.getDate() - 365); return d;
}

export default function ActivityAuditExecutive() {
  const [window, setWindow] = useState<TimeWindow>("today");
  const [dailySummary, setDailySummary] = useState<DailyRow[]>([]);
  const [pipelineCoverage, setPipelineCoverage] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const session = getSession();
  const tenantId = session?.tenantId ?? 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();
        const since = windowStartExec(window).toISOString();
        // Org activity aggregate
        const { data: logs } = await sb
          .from("activity_log")
          .select("action, channel, created_at")
          .eq("tenant_id", tenantId)
          .neq("tenant_id", 2)
          .gte("created_at", since);
        // Pipeline coverage from v_ae_pipeline_summary + sales_plans
        const { data: pipeData } = await sb
          .from("v_ae_pipeline_summary")
          .select("weighted_pipeline")
          .eq("tenant_id", tenantId);
        const { data: planData } = await sb
          .from("sales_plans")
          .select("q4")
          .eq("plan_year", new Date().getFullYear());
        if (!cancelled) {
          // Build daily summary
          const byDay = new Map<string, DailyRow>();
          for (const l of (logs ?? []) as any[]) {
            const day = l.created_at.slice(0, 10);
            if (!byDay.has(day)) byDay.set(day, { date: day, conversations: 0, meetings_booked: 0, deals_advanced: 0, deals_closed: 0 });
            const r = byDay.get(day)!;
            if (l.action && ["connected","replied","booked"].some(k => l.action.toLowerCase().includes(k))) r.conversations++;
            if (l.action && l.action.toLowerCase().includes("booked")) r.meetings_booked++;
          }
          setDailySummary(Array.from(byDay.values()).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 7));
          // Real pipeline coverage: est_arr (from accounts) / Q4 quota
          const totalPipe = (pipeData ?? []).reduce((s, p: any) => s + Number(p.weighted_pipeline ?? 0), 0);
          const totalQ4 = (planData ?? []).reduce((s, p: any) => s + Number(p.q4 ?? 0), 0);
          setPipelineCoverage(totalQ4 > 0 ? parseFloat((totalPipe / totalQ4).toFixed(2)) : null);
        }
      } catch {
        if (!cancelled) { setDailySummary([]); setPipelineCoverage(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [window, tenantId]);

  const kpis = useMemo(() => ({
    org_conversations: dailySummary.reduce((s, r) => s + r.conversations, 0),
    org_meetings_booked: dailySummary.reduce((s, r) => s + r.meetings_booked, 0),
    org_deals_advanced: 0,
    org_deals_closed: 0,
    pipeline_coverage: pipelineCoverage ?? 0,
  }), [dailySummary, pipelineCoverage]);
  const priorKpis = { org_conversations: 0, org_meetings_booked: 0, org_deals_advanced: 0, org_deals_closed: 0 };

  const coverageColor =
    kpis.pipeline_coverage >= 2
      ? tokens.color.success
      : kpis.pipeline_coverage >= 1.5
        ? tokens.color.amber
        : tokens.color.danger;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Isolation notice */}
      <div
        style={{
          background: "#F0FDF4", border: "1px solid #BBF7D0",
          borderRadius: 6, padding: "7px 14px",
          fontSize: 12, color: "#166534", fontWeight: 500,
        }}
      >
        Executive view — aggregate signals only. No individual attribution in this view.
        To see per-rep data, use the role switcher to access Manager view.
      </div>

      {/* Status banner — honest about data state (Track D B10 patch) */}
      {loading && (
        <div style={{ background: "#FAFAF7", border: "1px solid #EFEFEE", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#8E8E94" }}>
          Loading org activity...
        </div>
      )}
      {!loading && kpis.org_conversations === 0 && (
        <div style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#0369A1", fontWeight: 500 }}>
          No org activity logged in this window. Activity appears here once the log write paths are connected (C5/C6/C7).
        </div>
      )}

      {/* Zone 1 — Window selector */}
      <AuditWindowSelector active={window} onChange={setWindow} />

      {/* Zone 2 — KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        <AuditKpiCard
          label="Org Conversations"
          value={kpis.org_conversations}
          prior={priorKpis.org_conversations}
          kpiKey="org_conversations"
          window={window}
          drillRole="executive"
        />
        <AuditKpiCard
          label="Org Meetings Booked"
          value={kpis.org_meetings_booked}
          prior={priorKpis.org_meetings_booked}
          kpiKey="org_meetings_booked"
          window={window}
          drillRole="executive"
        />
        <AuditKpiCard
          label="Org Deals Advanced"
          value={kpis.org_deals_advanced}
          prior={priorKpis.org_deals_advanced}
          kpiKey="org_deals_advanced"
          window={window}
          drillRole="executive"
        />
        <AuditKpiCard
          label="Org Deals Closed"
          value={kpis.org_deals_closed}
          prior={priorKpis.org_deals_closed}
          kpiKey="org_deals_closed"
          window={window}
          drillRole="executive"
        />

        {/* Pipeline coverage ratio — special card */}
        <div
          style={{
            background: "#FFFFFF",
            border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.card,
            padding: "16px 18px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minHeight: 120,
            boxShadow: tokens.shadow.card,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, color: tokens.color.textMuted }}>
            Pipeline Coverage
          </div>
          <div style={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: 32, color: coverageColor, fontVariantNumeric: "tabular-nums" }}>
            {kpis.pipeline_coverage.toFixed(1)}x
          </div>
          <div style={{ fontSize: 12, color: tokens.color.textMuted, fontWeight: 500 }}>
            {pipelineCoverage != null ? `Real pipeline coverage (weighted)` : `Coverage data activates once sales plans are set`}
          </div>
          <div style={{ height: 4, background: "#EFEFEE", borderRadius: 999, overflow: "hidden", marginTop: 4 }}>
            <div style={{ width: `${Math.min(100, (kpis.pipeline_coverage / 3) * 100)}%`, height: "100%", background: coverageColor, borderRadius: 999 }} />
          </div>
        </div>
      </div>

      {/* Zone 3 — Org-level line chart placeholder */}
      <div
        style={{
          background: "#FFFFFF", border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.card, padding: "18px 20px",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary, marginBottom: 12 }}>
          Org Activity Volume — 5-Day Trend
        </div>

        {/* Simple SVG line chart */}
        <svg width="100%" viewBox="0 0 600 140" style={{ display: "block" }}>
          {/* Grid lines */}
          {[0, 4, 8, 12].map((y, i) => (
            <g key={i}>
              <line x1={40} x2={580} y1={20 + i * 28} y2={20 + i * 28} stroke="#EFEFEE" strokeWidth={1} />
              <text x={34} y={24 + i * 28} textAnchor="end" fontSize={9} fill="#8E8E94">{12 - i * 4}</text>
            </g>
          ))}
          {/* Conversations line — real data from dailySummary */}
          {(() => {
            const pts = dailySummary.slice().reverse();
            if (pts.length < 2) return <text x={320} y={70} textAnchor="middle" fontSize={11} fill="#8E8E94">No activity data for this window</text>;
            const max = Math.max(...pts.map(d => d.conversations), 1);
            const xs = pts.map((_, i) => 40 + (i / (pts.length - 1)) * 540);
            const ys = pts.map((d) => 104 - (d.conversations / max) * 84);
            const path = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x} ${ys[i]}`).join(" ");
            return (
              <>
                <path d={path} fill="none" stroke={tokens.color.navy} strokeWidth={2} strokeLinejoin="round" />
                {pts.map((d, i) => (
                  <g key={i}>
                    <circle cx={xs[i]} cy={ys[i]} r={4} fill={tokens.color.navy} />
                    <text x={xs[i]} y={125} textAnchor="middle" fontSize={9} fill="#8E8E94">{d.date.slice(5)}</text>
                  </g>
                ))}
              </>
            );
          })()}
        </svg>

        <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: tokens.color.textMuted }}>
            <span style={{ width: 10, height: 2, background: tokens.color.black, display: "inline-block" }} /> Conversations
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: tokens.color.textMuted }}>
            <span style={{ width: 10, height: 2, background: tokens.color.red, display: "inline-block", borderTop: "2px dashed" }} /> Deals Advanced
          </div>
        </div>
      </div>

      {/* Zone 4 — Aggregate daily summary table (NO individual rows) */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: tokens.color.textPrimary, marginBottom: 10 }}>
          Aggregate Daily Summary
        </div>
        <div style={{ fontSize: 11, color: tokens.color.textSubtle, marginBottom: 8 }}>
          Org-level totals only. No individual-level data in this view.
        </div>

        <div
          style={{
            background: "#FFFFFF", border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.card, overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid", gridTemplateColumns: "100px 1fr 1fr 1fr 1fr",
              gap: 12, padding: "9px 16px",
              borderBottom: `1px solid ${tokens.color.border}`,
              background: tokens.color.surfaceMute,
            }}
          >
            {["Date", "Conversations", "Meetings Booked", "Deals Advanced", "Deals Closed"].map((h) => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: tokens.color.textSubtle }}>{h}</div>
            ))}
          </div>

          {dailySummary.length === 0 && !loading && (
            <div style={{ padding: "24px 16px", textAlign: "center", fontSize: 13, color: tokens.color.textSubtle }}>
              No org activity for this window.
            </div>
          )}
          {dailySummary.map((row) => (
            <div
              key={row.date}
              style={{
                display: "grid", gridTemplateColumns: "100px 1fr 1fr 1fr 1fr",
                gap: 12, padding: "11px 16px",
                borderBottom: `1px solid ${tokens.color.borderSoft}`,
                alignItems: "center",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = tokens.color.surfaceMute; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: tokens.color.textMuted }}>{row.date}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary, fontVariantNumeric: "tabular-nums" }}>{row.conversations}</div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>{row.meetings_booked}</div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>{row.deals_advanced}</div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>{row.deals_closed}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
